/**
 * UIParticleSystem - Reusable particle effects for UI interactions
 *
 * Creates particle animations that flow from a source to a target element,
 * with configurable physics, colors, and target effects (shake, glow, value update).
 */

export class UIParticleSystem {
    constructor(gameUI) {
        this.gameUI = gameUI;

        // Sound pool for lerp/absorb effects
        this.lerpSoundPool = [];
        for (let i = 0; i < 8; i++) {
            const sound = new Audio('assets/sfx/pip3.mp3');
            sound.volume = 0.15;
            this.lerpSoundPool.push(sound);
        }
        this.soundIndex = 0;

        // Track active animations for cleanup
        this._activeAnimations = new Set();

        // Register pip sounds with SFXManager
        this._registerSounds();
    }

    /**
     * Clean up all active animations (call when menu closes)
     */
    cleanup() {
        for (const anim of this._activeAnimations) {
            if (anim.rafId) {
                cancelAnimationFrame(anim.rafId);
            }
            if (anim.soundInterval) {
                clearInterval(anim.soundInterval);
            }
            if (anim.container && anim.container.parentNode) {
                anim.container.remove();
            }
        }
        this._activeAnimations.clear();
    }

    /**
     * Warm up the particle system by creating an invisible test animation.
     * This preloads browser rendering pipelines to avoid first-use stutter.
     */
    warmUp() {
        if (this._warmedUp) return;
        this._warmedUp = true;

        // Create a quick off-screen particle burst
        const container = document.createElement('div');
        container.style.cssText = `
            position: fixed;
            top: -1000px;
            left: -1000px;
            width: 100px;
            height: 100px;
            pointer-events: none;
            z-index: -1;
            opacity: 0;
        `;
        document.body.appendChild(container);

        // Create a few particles with all the CSS effects we use
        const gradient = `radial-gradient(circle, #fff 0%, #ffd700 50%, #ff8c00 100%)`;
        for (let i = 0; i < 5; i++) {
            const particle = document.createElement('div');
            particle.style.cssText = `
                position: absolute;
                width: 8px;
                height: 8px;
                background: ${gradient};
                border-radius: 50%;
                box-shadow: 0 0 8px #ffd700;
                left: 50px;
                top: 50px;
            `;
            container.appendChild(particle);
        }

        // Force browser to render, then clean up
        container.offsetHeight; // Force layout
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                container.remove();
            });
        });
    }

    _registerSounds() {
        const sfx = this.gameUI?.sfxManager;
        if (!sfx) return;

        for (let i = 0; i < this.lerpSoundPool.length; i++) {
            sfx.registerSound(`pip${i}`, this.lerpSoundPool[i]);
        }
    }

    _playPip() {
        // Disabled - mile counter handles pip sounds now
        return;
    }

    /**
     * Default configuration for gold upgrade particles
     */
    static get DEFAULTS() {
        return {
            // Particle appearance
            particleCount: 12,
            particleSizeMin: 4,
            particleSizeMax: 12,
            color: '#ffd700',
            colorGradient: ['#fff', '#ffd700', '#ff8c00'], // center, mid, edge

            // Physics
            initialSpeedMin: 150,
            initialSpeedMax: 350,
            initialLift: 50, // upward bias
            gravity: 100,
            gravityVariance: 50,
            drag: 0.98,
            swayStrength: 100,

            // Timing
            flightDuration: 600,
            homeStartMin: 200,
            homeStartMax: 400,
            shakeDuration: 600,

            // Target effects
            shakeIntensity: 1.0,
            glowIntensity: 10,
            playSound: true,
            soundInterval: 70,
        };
    }

    /**
     * Preset for item upgrades (purple/blue tint, faster, smaller)
     */
    static get ITEM_PRESET() {
        return {
            particleCount: 8,
            particleSizeMin: 3,
            particleSizeMax: 8,
            initialSpeedMin: 100,
            initialSpeedMax: 250,
            initialLift: 30,
            gravity: 80,
            gravityVariance: 20,
            flightDuration: 400,
            homeStartMin: 100,
            homeStartMax: 250,
            shakeDuration: 300,
            shakeIntensity: 1.5,
            glowIntensity: 8,
            playSound: false,
        };
    }

    /**
     * Preset for celebration bursts (radial explosion, no homing)
     */
    static get CELEBRATION_PRESET() {
        return {
            particleCount: 16,
            particleSizeMin: 3,
            particleSizeMax: 9,
            initialSpeedMin: 80,
            initialSpeedMax: 200,
            radialBurst: true, // particles spread outward, no homing
            flightDuration: 450,
            fadeOut: true,
        };
    }

    /**
     * Preset for scrap actions (green mile particles)
     */
    static get SCRAP_PRESET() {
        return {
            particleCount: 12,
            particleSizeMin: 3,
            particleSizeMax: 7,
            color: '#50ff50',
            colorGradient: ['#ffffff', '#90ff90', '#50ff50', '#30cc30'],
            initialSpeedMin: 120,
            initialSpeedMax: 240,
            initialLift: 35,
            gravity: 70,
            gravityVariance: 25,
            drag: 0.97,
            flightDuration: 400,
            homeStartMin: 120,
            homeStartMax: 200,
            shakeDuration: 200,
            shakeIntensity: 0.5,
            glowIntensity: 4,
            playSound: true,
            soundInterval: 60,
        };
    }

    /**
     * Create particles that flow from source to target
     * @param {object} options Configuration options
     * @param {number} options.startX - Start X position
     * @param {number} options.startY - Start Y position
     * @param {HTMLElement|{x,y}} options.target - Target element or {x, y} coordinates
     * @param {string} [options.color] - Particle color
     * @param {string} [options.newValue] - New value to set on target after animation
     * @param {function} [options.onArrive] - Callback when particles arrive
     * @param {function} [options.onComplete] - Callback when animation completes
     * @param {object} [options.progressBar] - Progress bar config {element, fill, oldValue, newValue}
     * @param {object} [options.tierChange] - Tier change config {badge, newTier, newColor}
     * @param {string} [options.preset] - 'default', 'item', 'celebration', or 'scrap'
     * @param {...} - Any other options override defaults
     */
    createParticles(options) {
        // Merge preset and custom options with defaults
        let config = { ...UIParticleSystem.DEFAULTS };

        if (options.preset === 'item') {
            config = { ...config, ...UIParticleSystem.ITEM_PRESET };
        } else if (options.preset === 'celebration') {
            config = { ...config, ...UIParticleSystem.CELEBRATION_PRESET };
        } else if (options.preset === 'scrap') {
            config = { ...config, ...UIParticleSystem.SCRAP_PRESET };
        }

        config = { ...config, ...options };

        // Handle radial burst (celebration) separately
        if (config.radialBurst) {
            return this._createRadialBurst(config);
        }

        // Get target position
        let targetX, targetY;
        let targetElement = null;

        if (config.target instanceof HTMLElement) {
            targetElement = config.target;
            const rect = config.target.getBoundingClientRect();
            targetX = rect.left + rect.width / 2;
            targetY = rect.top + rect.height / 2;
        } else if (config.target && typeof config.target.x === 'number') {
            targetX = config.target.x;
            targetY = config.target.y;
            // Allow specifying a selector to find the element for effects (shake/glow)
            // This is useful when the original element may be rebuilt during animation
            if (config.targetElementSelector) {
                targetElement = document.querySelector(config.targetElementSelector);
            }
        } else {
            console.warn('UIParticleSystem: No valid target specified');
            return;
        }

        // Create particle container
        const container = document.createElement('div');
        container.className = 'ui-particle-container';
        container.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 10001;
        `;
        document.body.appendChild(container);

        // Build color gradient string
        const gradient = config.colorGradient || ['#fff', config.color, config.color + '88'];
        const gradientStr = `radial-gradient(circle, ${gradient[0]} 0%, ${gradient[1]} 50%, ${gradient[2]} 100%)`;

        // Create particles
        const particles = [];
        for (let i = 0; i < config.particleCount; i++) {
            const particle = document.createElement('div');
            const size = config.particleSizeMin + Math.random() * (config.particleSizeMax - config.particleSizeMin);

            particle.style.cssText = `
                position: absolute;
                width: ${size}px;
                height: ${size}px;
                background: ${gradientStr};
                border-radius: 50%;
                box-shadow: 0 0 ${size}px ${config.color};
                left: ${config.startX}px;
                top: ${config.startY}px;
            `;
            container.appendChild(particle);

            const angle = Math.random() * Math.PI * 2;
            const speed = config.initialSpeedMin + Math.random() * (config.initialSpeedMax - config.initialSpeedMin);

            particles.push({
                el: particle,
                x: config.startX,
                y: config.startY,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - config.initialLift,
                sway: (Math.random() - 0.5) * config.swayStrength,
                gravity: config.gravity + Math.random() * config.gravityVariance,
                drag: config.drag,
                homeStart: config.homeStartMin + Math.random() * (config.homeStartMax - config.homeStartMin),
                size: size
            });
        }

        // Animation state (tracked for cleanup)
        const animState = {
            container: container,
            soundInterval: null,
            rafId: null
        };
        this._activeAnimations.add(animState);

        const startTime = performance.now();
        let lastTime = startTime;
        let shakeStarted = false;

        // For progress bar animations
        let displayedProgress = config.progressBar?.oldValue ?? 0;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const dt = Math.min((currentTime - lastTime) / 1000, 0.05);
            lastTime = currentTime;

            // Phase 1: Particles fly toward target
            if (elapsed < config.flightDuration) {
                particles.forEach((p) => {
                    const homeT = Math.max(0, Math.min(1, (elapsed - p.homeStart) / (config.flightDuration - p.homeStart)));
                    const homeStrength = homeT * homeT; // Ease in

                    const driftFactor = 1 - homeStrength;
                    p.vx += p.sway * dt * driftFactor;
                    p.vy += p.gravity * dt * driftFactor;
                    p.vx *= p.drag;
                    p.vy *= p.drag;

                    const physicsX = p.x + p.vx * dt;
                    const physicsY = p.y + p.vy * dt;

                    p.x = physicsX + (targetX - physicsX) * homeStrength * 0.15;
                    p.y = physicsY + (targetY - physicsY) * homeStrength * 0.15;

                    const scale = 1 - homeT * 0.6;
                    p.el.style.left = `${p.x}px`;
                    p.el.style.top = `${p.y}px`;
                    p.el.style.transform = `scale(${scale})`;
                    p.el.style.opacity = 1 - homeT * 0.5;
                });

                animState.rafId = requestAnimationFrame(animate);
            }
            // Phase 2: Shake target and update value
            else if (elapsed < config.flightDuration + config.shakeDuration) {
                particles.forEach(p => p.el.style.opacity = 0);

                if (!shakeStarted) {
                    shakeStarted = true;

                    // Re-query target element if selector provided (element may have been rebuilt)
                    if (config.targetElementSelector) {
                        targetElement = document.querySelector(config.targetElementSelector);
                    }

                    // Update target value if specified
                    if (config.newValue !== undefined && targetElement) {
                        targetElement.textContent = config.newValue;
                    }

                    // Add glow effect
                    if (targetElement && config.glowIntensity > 0) {
                        targetElement.style.textShadow = `0 0 ${config.glowIntensity}px ${config.color}`;
                    }

                    // Start sound if enabled (for tier changes, play levelAvail immediately)
                    if (config.tierChange) {
                        this.gameUI?.sfxManager?.playSound('levelAvail');
                    } else if (config.playSound) {
                        animState.soundInterval = setInterval(() => {
                            this._playPip();
                        }, config.soundInterval);
                    }

                    // Callback
                    if (config.onArrive) {
                        config.onArrive();
                    }
                }

                const shakeElapsed = elapsed - config.flightDuration;
                const shakeT = shakeElapsed / config.shakeDuration;
                const eased = shakeT * shakeT * (3 - 2 * shakeT); // Smooth step

                // Shake effect
                const shakePhase = (shakeElapsed / 25) | 0;
                const shakePattern = [0, 2, -2, 1, -1, 2, -2, 0, 1, -1];
                const shakeX = shakePattern[shakePhase % shakePattern.length] * (1 - shakeT) * config.shakeIntensity;

                if (targetElement) {
                    targetElement.style.transform = `translateX(${shakeX}px)`;
                }

                // Progress bar animation
                if (config.progressBar) {
                    const pb = config.progressBar;
                    // If tier change, fill to 100% instead of lerping to new value
                    if (config.tierChange) {
                        displayedProgress = pb.oldValue + (1 - pb.oldValue) * eased;
                    } else {
                        displayedProgress = pb.oldValue + (pb.newValue - pb.oldValue) * eased;
                    }
                    if (pb.fill) {
                        pb.fill.style.width = `${Math.round(displayedProgress * 100)}%`;
                    }
                    if (pb.element) {
                        pb.element.style.transform = `translateX(${shakeX}px)`;
                        const glowIntensity = (1 - shakeT) * config.glowIntensity;
                        pb.fill.style.boxShadow = `0 0 ${glowIntensity}px ${config.color}`;
                    }
                }

                animState.rafId = requestAnimationFrame(animate);
            }
            // Phase 3: Tier celebration (optional)
            else if (config.tierChange && elapsed < config.flightDuration + config.shakeDuration + 500) {
                if (!config._tierCelebrationStarted) {
                    config._tierCelebrationStarted = true;
                    if (animState.soundInterval) {
                        clearInterval(animState.soundInterval);
                        animState.soundInterval = null;
                    }

                    // Update tier badge
                    const tc = config.tierChange;
                    if (tc.badge) {
                        tc.badge.textContent = tc.newTier.toUpperCase();
                        tc.badge.style.color = tc.newColor;
                        tc.badge.style.borderColor = tc.newColor;
                    }

                    // Reset progress bar to new tier's progress instantly
                    if (config.progressBar && config.progressBar.fill) {
                        config.progressBar.fill.style.width = `${Math.round(config.progressBar.newValue * 100)}%`;
                    }

                    // Create celebration burst
                    this.createParticles({
                        preset: 'celebration',
                        startX: targetX,
                        startY: targetY,
                        color: tc.newColor,
                    });
                }

                animState.rafId = requestAnimationFrame(animate);
            }
            // Phase 4: Cleanup
            else {
                if (animState.soundInterval) {
                    clearInterval(animState.soundInterval);
                    animState.soundInterval = null;
                }
                container.remove();
                this._activeAnimations.delete(animState);

                if (targetElement) {
                    targetElement.style.transform = '';
                    targetElement.style.textShadow = '';
                }

                if (config.progressBar) {
                    config.progressBar.element.style.transform = '';
                    config.progressBar.fill.style.boxShadow = '';
                }

                if (config.onComplete) {
                    config.onComplete();
                }
            }
        };

        animState.rafId = requestAnimationFrame(animate);
        return container;
    }

    /**
     * Create radial burst particles (for celebrations)
     */
    _createRadialBurst(config) {
        const container = document.createElement('div');
        container.className = 'ui-particle-container';
        container.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 10001;
        `;
        document.body.appendChild(container);

        const centerX = config.startX;
        const centerY = config.startY;
        const particles = [];

        // Pre-calculate radial directions
        const directions = [];
        for (let i = 0; i < config.particleCount; i++) {
            const segment = i / config.particleCount;
            const dirLookupX = [1, 0.7, 0, -0.7, -1, -0.7, 0, 0.7];
            const dirLookupY = [0, 0.7, 1, 0.7, 0, -0.7, -1, -0.7];
            const idx = (i * 8 / config.particleCount) | 0;
            directions.push({
                x: dirLookupX[idx % 8] + ((i % 3) - 1) * 0.2,
                y: dirLookupY[idx % 8] + ((i % 2) - 0.5) * 0.2
            });
        }

        // Create particles
        for (let i = 0; i < config.particleCount; i++) {
            const particle = document.createElement('div');
            const size = config.particleSizeMin + (i % 4) * ((config.particleSizeMax - config.particleSizeMin) / 3);

            particle.style.cssText = `
                position: absolute;
                width: ${size}px;
                height: ${size}px;
                background: ${config.color};
                border-radius: 50%;
                box-shadow: 0 0 ${size + 2}px ${config.color};
                left: ${centerX}px;
                top: ${centerY}px;
                opacity: 1;
            `;
            container.appendChild(particle);

            particles.push({
                el: particle,
                dir: directions[i],
                speed: config.initialSpeedMin + (i % 5) * ((config.initialSpeedMax - config.initialSpeedMin) / 4),
                startTime: performance.now() + (i % 4) * 15
            });
        }

        // Track animation for cleanup
        const animState = {
            container: container,
            soundInterval: null,
            rafId: null
        };
        this._activeAnimations.add(animState);

        // Animate outward
        const duration = config.flightDuration;
        const animateParticles = () => {
            const now = performance.now();
            let anyAlive = false;

            particles.forEach(p => {
                const elapsed = now - p.startTime;
                if (elapsed < 0) {
                    anyAlive = true;
                    return;
                }
                if (elapsed > duration) {
                    p.el.style.opacity = 0;
                    return;
                }

                anyAlive = true;
                const t = elapsed / duration;
                const eased = 1 - (1 - t) * (1 - t); // Ease out

                const x = centerX + p.dir.x * p.speed * eased;
                const y = centerY + p.dir.y * p.speed * eased;

                p.el.style.left = `${x}px`;
                p.el.style.top = `${y}px`;
                p.el.style.opacity = 1 - eased;
                p.el.style.transform = `scale(${1 - eased * 0.5})`;
            });

            if (anyAlive) {
                animState.rafId = requestAnimationFrame(animateParticles);
            } else {
                container.remove();
                this._activeAnimations.delete(animState);
                if (config.onComplete) {
                    config.onComplete();
                }
            }
        };

        animState.rafId = requestAnimationFrame(animateParticles);
        return container;
    }

    /**
     * Convenience method for item upgrade particles
     */
    createItemParticles(startX, startY, target, newValue, color, options = {}) {
        return this.createParticles({
            startX,
            startY,
            target,
            newValue,
            color,
            ...options
        });
    }
}
